import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy } from 'react-icons/fa';
import { CheckmarkIcon } from 'react-hot-toast';
import { Tooltip, Button } from 'antd';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import styled from '@emotion/styled';
import Datestamp from '../../../components/Datestamp';
import { useIsClientAdmin } from '../../../hooks/useIsClientAdmin';
import useIsClinician from '../../../hooks/useIsClinician';
import { differenceInYears } from 'date-fns';
import { PatientActionMenu } from '../../Menus/PatientActionMenu';
import ManagePatientContextProvider, { useManagePatientContext } from '../../../lib/context/ManagePatientContext';
import CaseManagementProvider, { useManageCaseContext } from '../../../lib/context/ManageCaseContext';
import { Case } from '../../../schemas/shared';

const TitleActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PatientTitle = styled.h2`
  font-size: 24px;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PatientInfoGrid = styled.div<{ fieldsCount: number }>`
  display: grid;
  grid-template-columns: repeat(${({ fieldsCount }) => Math.ceil(fieldsCount / 2)}, minmax(200px, max-content));
  gap: 1rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldInfoIconContainer = styled.span`
  margin-left: 0.5rem;
  font-size: 16px;
  color: var(--productPrimary);
`;

const StyledDetail = styled.div`
  font-size: 14px;
`;

const FieldName = styled.div`
  font-weight: bold;
`;

const getDocmanConsentText = (consentValue?: boolean | null) => {
  switch (consentValue) {
    case true: {
      return 'Yes';
    }
    case false: {
      return 'Withheld consent';
    }
    default: {
      return 'Unavailable';
    }
  }
};

const getFullAddress = (patient: any) => {
  return [
    patient?.addline1,
    patient?.addline2,
    patient?.addline3,
    patient?.addline4,
    patient?.region,
    patient?.postcode
  ]
    .filter(Boolean)
    .join(', ');
};

const DocmanConsentTooltipContent = () => {
  const { t } = useTranslation('common');
  return (
    <>
      <p>{t('Indicates whether the patient has consented to automatically sharing case docume')}</p>
      <ul style={{ padding: 0 }}>
        <li>
          <strong>{t('Yes:')}</strong> {t('The patient has consented to automatic sharing.')}
        </li>
        <li>
          <strong>{t('Withheld consent:')}</strong> {t(' The patient has not consented to automatic sharing.')}
        </li>
        <li>
          <strong>{t('Unavailable:')}</strong>{' '}
          {t('The patient has not yet responded to the consent question, or automatic sharing')}
        </li>
      </ul>
    </>
  );
};

type PatientInformationProps = {
  patient: any;
  docman_consent?: boolean | null;
  onNewCaseCreated?: (caseId: number) => Promise<void>;
  selectedCase?: Case | null;
};

type PatientInfoItem = {
  label: string;
  dataTestId: string;
  content: React.ReactNode;
  hidden?: boolean;
};

const PatientInformationInner = ({
  patient,
  docman_consent,
  onNewCaseCreated,
  selectedCase
}: PatientInformationProps) => {
  const { t } = useTranslation('common');
  const [linkCopied, setLinkCopied] = useState(false);
  const isClientAdmin = useIsClientAdmin();
  const isClinician = useIsClinician();

  const { onSetActionedPatient, openCreateCaseModal } = useManagePatientContext();
  const { onSetActionedCase, onBookCaseAppointment } = useManageCaseContext();

  const patientAge = differenceInYears(new Date(), new Date(patient?.dob));

  const copyLinkToClipboard = () => {
    const url = new URL(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 5000);
    navigator.clipboard.writeText(url.href);
  };

  const handleCreateCase = () => {
    onSetActionedPatient({
      patient,
      refetchFn: (params) => {
        if (params?.createdCaseId) {
          onNewCaseCreated?.(params.createdCaseId);
        }
      }
    });
    openCreateCaseModal();
  };

  const handleBookAppointment = () => {
    if (!selectedCase) return;
    onSetActionedCase({ activeCase: selectedCase });
    onBookCaseAppointment(selectedCase);
  };

  const isBookable = !!selectedCase?.appt_bookable;

  const patientFullAddress = getFullAddress(patient);
  const infoItems: PatientInfoItem[] = [
    {
      label: t('Preferred Name:'),
      dataTestId: 'patient-info-pref-name',
      content: patient?.pref_name
    },
    {
      label: t('VL ID:'),
      dataTestId: 'patient-info-vl-id',
      content: patient?.lucy_id,
      hidden: !isClientAdmin
    },
    {
      label: t('Member Number:'),
      dataTestId: 'patient-info-mem-no',
      content: patient?.membership_no ? patient.membership_no : t('Unavailable')
    },
    {
      label: t('Status:'),
      dataTestId: 'patient-info-status',
      content: patient?.status,
      hidden: isClinician
    },
    {
      label: t('Postcode:'),
      dataTestId: 'patient-info-postcode',
      content: patient?.postcode,
      hidden: isClientAdmin
    },
    {
      label: t('Address:'),
      dataTestId: 'patient-info-address',
      content: (
        <>
          {[patient?.addline1, patient?.postcode].filter(Boolean).join(', ')}
          {!!patientFullAddress && (
            <Tooltip title={patientFullAddress} trigger={['hover', 'focus']}>
              <FieldInfoIconContainer tabIndex={0}>
                <InfoCircleOutlined aria-hidden />
              </FieldInfoIconContainer>
            </Tooltip>
          )}
        </>
      ),
      hidden: !isClientAdmin
    },
    {
      label: t('Date of Birth:'),
      dataTestId: 'patient-info-dob',
      content: patient?.dob ? (
        <>
          <Datestamp date={patient.dob} format="Date only (localised) - 03/02/2022" />{' '}
          {t('({{patientAge}} years old)', { patientAge })}
        </>
      ) : (
        t('Unavailable')
      )
    },
    {
      label: t('Phone Number:'),
      dataTestId: 'patient-info-mobile-no',
      content: patient?.tel_mobile_no ? patient.tel_mobile_no : t('Unavailable'),
      hidden: isClinician
    },
    {
      label: t('Docman Consent:'),
      dataTestId: 'docman-consent-text',
      content: (
        <>
          {getDocmanConsentText(docman_consent)}
          <Tooltip title={<DocmanConsentTooltipContent />} trigger={['hover', 'focus']}>
            <FieldInfoIconContainer tabIndex={0}>
              <InfoCircleOutlined aria-hidden />
            </FieldInfoIconContainer>
          </Tooltip>
        </>
      ),
      hidden: !isClientAdmin
    }
  ];

  const filteredInfoItems = infoItems.filter((item) => !item?.hidden);

  return (
    <div>
      <TitleActionsSection>
        <PatientTitle>
          {t('Patient: ')}
          {patient?.title} {patient?.full_name}
          &nbsp;
          {!isClinician && patient?.user_guid && (
            <PatientActionMenu patientId={patient.user_guid} onNewCaseCreated={onNewCaseCreated} />
          )}
        </PatientTitle>
        <Actions>
          {!isClinician && (
            <>
              <Button
                onClick={handleBookAppointment}
                disabled={!isBookable}
                data-testid="admin-book-appointment"
                type="primary"
              >
                {t('Book Appointment')}
              </Button>

              <Button onClick={handleCreateCase} data-testid="admin-create-case" type="primary">
                {t('Create Case')}
              </Button>
            </>
          )}
          <Button
            onClick={copyLinkToClipboard}
            data-testid="copy-link-button"
            // @ts-ignore
            type={!linkCopied ? 'primary' : 'ghost'}
            style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}
          >
            {!linkCopied ? (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                <FaCopy /> {t('Timeline Link')}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                <CheckmarkIcon /> {t('Link Copied!')}
              </div>
            )}
          </Button>
        </Actions>
      </TitleActionsSection>
      <PatientInfoGrid fieldsCount={filteredInfoItems.length}>
        {filteredInfoItems.map(({ label, dataTestId, content }) => (
          <InfoItem key={dataTestId}>
            <StyledDetail>
              <FieldName>{label}</FieldName>
              <div data-testid={dataTestId}>{content}</div>
            </StyledDetail>
          </InfoItem>
        ))}
      </PatientInfoGrid>
    </div>
  );
};

export const PatientInformation = (props: PatientInformationProps) => (
  <ManagePatientContextProvider>
    <CaseManagementProvider>
      <PatientInformationInner {...props} />
    </CaseManagementProvider>
  </ManagePatientContextProvider>
);
